import { useNuxtApp } from '#app'
import type { PremiumPlan } from '~/types/index'

export function useProducts() {
	const planMonthly = ref<PremiumPlan | null>(null)
	const planYearly = ref<PremiumPlan | null>(null)
	const pass = ref()

	const planPriceMonthly = ref<StripePricePlan | null>(null)
	const planPriceYearly = ref<StripePricePlan | null>(null)
	const passPrice = ref<StripePricePlan | null>(null)

	const beforeCurrencies = ['usd', 'cad', 'aud', 'gbp', 'nok', 'mxn', 'uah']

	const countryToCurrency: Record<string, { code: string, symbol: string }> = {
		us: { code: 'usd', symbol: '$' },
		ca: { code: 'cad', symbol: '$' },
		au: { code: 'aud', symbol: '$' },
		gb: { code: 'gbp', symbol: '£' },
		se: { code: 'sek', symbol: 'kr' },
		no: { code: 'nok', symbol: 'kr' },
		is: { code: 'isk', symbol: 'kr' },
		dk: { code: 'dkk', symbol: 'kr' },
		ro: { code: 'ron', symbol: 'lei' },
		pl: { code: 'pln', symbol: 'zł' },
		ch: { code: 'chf', symbol: 'fr.' },
		hu: { code: 'huf', symbol: 'Ft' },
		cz: { code: 'czk', symbol: 'Kč' },
		bg: { code: 'bgn', symbol: 'лв' },
		ph: { code: 'php', symbol: '₱' },
		mx: { code: 'mxn', symbol: '$' },
		ua: { code: 'uah', symbol: '₴' },
		ru: { code: 'rub', symbol: 'RUB' },
		as: { code: 'usd', symbol: '$' },
		at: { code: 'eur', symbol: '€' },
		aw: { code: 'awg', symbol: 'ƒ' },
		ax: { code: 'eur', symbol: '€' },
		be: { code: 'eur', symbol: '€' },
		bl: { code: 'eur', symbol: '€' },
		cw: { code: 'ang', symbol: 'ƒ' },
		cy: { code: 'eur', symbol: '€' },
		de: { code: 'eur', symbol: '€' },
		ee: { code: 'eur', symbol: '€' },
		es: { code: 'eur', symbol: '€' },
		fi: { code: 'eur', symbol: '€' },
		fo: { code: 'dkk', symbol: 'kr' },
		fr: { code: 'eur', symbol: '€' },
		gf: { code: 'eur', symbol: '€' },
		gl: { code: 'dkk', symbol: 'kr' },
		gp: { code: 'eur', symbol: '€' },
		gr: { code: 'eur', symbol: '€' },
		gu: { code: 'usd', symbol: '$' },
		ie: { code: 'eur', symbol: '€' },
		it: { code: 'eur', symbol: '€' },
		li: { code: 'chf', symbol: 'fr.' },
		lt: { code: 'eur', symbol: '€' },
		lu: { code: 'eur', symbol: '€' },
		lv: { code: 'eur', symbol: '€' },
		mf: { code: 'eur', symbol: '€' },
		mp: { code: 'usd', symbol: '$' },
		mq: { code: 'eur', symbol: '€' },
		mt: { code: 'eur', symbol: '€' },
		nc: { code: 'xpf', symbol: '₣' },
		nl: { code: 'eur', symbol: '€' },
		pf: { code: 'xpf', symbol: '₣' },
		pm: { code: 'eur', symbol: '€' },
		pr: { code: 'usd', symbol: '$' },
		pt: { code: 'eur', symbol: '€' },
		re: { code: 'eur', symbol: '€' },
		si: { code: 'eur', symbol: '€' },
		sk: { code: 'eur', symbol: '€' },
		sx: { code: 'ang', symbol: 'ƒ' },
		tf: { code: 'eur', symbol: '€' },
		vi: { code: 'usd', symbol: '$' },
		wf: { code: 'xpf', symbol: '₣' },
		yt: { code: 'eur', symbol: '€' }
	}

	type CurrencyCode = typeof countryToCurrency[keyof typeof countryToCurrency]['code']

	const getCurrencyByCountry = (countryCode: string): { code: CurrencyCode, symbol: string } | undefined => {
		return countryToCurrency[countryCode.toLowerCase()]
	}

	const formattedCurrency = (amount: number, currency: CurrencyCode) => {
		const currencyEntry = Object.values(countryToCurrency).find(entry => entry.code === currency)
		const symbol = currencyEntry?.symbol || currency.toUpperCase() // Use the symbol directly
		const formattedAmount = amount === 0 ? '0' : (amount / 100).toFixed(2)
		const currencyClass = 'currency-symbol'

		return beforeCurrencies.includes(currency)
			? `<span class="${currencyClass}"> ${symbol} </span>${formattedAmount}`
			: `${formattedAmount} <span class="${currencyClass}"> ${symbol} </span>`
	}

	const getUserCurrency = async (country: string) => {
		if (import.meta.prerender) { return }
		return countryToCurrency[country.toLowerCase()]
	}

	const getPlans = async () => {
		if (import.meta.prerender) { return }
		const { $singaApi } = useNuxtApp()
		try {
			const response = await $singaApi.Products.getPlans({})
			// There's two plans with the interval 'month', Premium in the name ought to differentiate between them
			planMonthly.value = response.results?.find((plan: any) => plan.interval === 'month' && plan.name.includes('Premium'))
			planYearly.value = response.results?.find((plan: any) => plan.interval === 'year')

			if (planMonthly.value) {
				planMonthly.value.rawMonthlyAmount = planMonthly.value.amount
			}

			if (planYearly.value) {
				planYearly.value.rawMonthlyAmount = planYearly.value.amount / 12
			}
			return { monthly: planMonthly.value, yearly: planYearly.value }
		} catch (error) {
			console.error(error)
		}
	}

	const getPasses = async () => {
		if (import.meta.prerender) { return }
		const { $singaApi } = useNuxtApp()
		try {
			const response = await $singaApi.Products.getPasses({})
			pass.value = response.results[0]
			return pass.value
		} catch (error) {
			console.error(error)
		}
	}

	const getPassPrices = async () => {
		if (import.meta.prerender) { return }
		const { $singaApi } = useNuxtApp()
		const { countryCode } = useGeoLocationStore()
		if (!countryCode) {
			return console.error('No country available, unable to fetch pass price')
		}
		const currency = await getUserCurrency(countryCode)
		try {
			passPrice.value = await $singaApi.Products.getPassPrices({ currency: currency?.code })
			return passPrice.value
		} catch (error) {
			console.error(error)
		}
	}

	const getPricePlans = async () => {
		if (import.meta.prerender) { return }
		const { $singaApi } = useNuxtApp()
		try {
			const response = await $singaApi.Products.getPrices({})
			planPriceMonthly.value = response.results?.find((plan: any) => plan.recurring_interval === 'month' && plan.nickname.includes('Premium'))
			planPriceYearly.value = response.results?.find((plan: any) => plan.recurring_interval === 'year')
			if (planPriceMonthly.value) {
				planPriceMonthly.value.rawMonthlyAmount = planPriceMonthly.value.unit_amount
			}

			if (planPriceYearly.value) {
				planPriceYearly.value.rawMonthlyAmount = planPriceYearly.value.unit_amount / 12
			}
			return { monthly: planPriceMonthly.value, yearly: planPriceYearly.value }
		} catch (e) {
			console.log(e)
		}
	}

	return {
		planMonthly,
		planYearly,
		pass,
		planPriceMonthly,
		planPriceYearly,
		formattedCurrency,
		getPlans,
		getPasses,
		beforeCurrencies,
		getPricePlans,
		getPassPrices,
		passPrice,
		getCurrencyByCountry,
		countryToCurrency,
		getUserCurrency
	}
}
